/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Radio, Group, Text, Stack } from '@/shared/ui';
import { DataRightType, DATA_RIGHTS_OPTIONS } from '../types/rights-types';

interface RightsTypeSelectorProps {
  selectedType: DataRightType | null;
  onSelect: (type: DataRightType) => void;
}

/**
 * RightsTypeSelector component
 * Allows users to select which data subject right they want to exercise
 */
export const RightsTypeSelector: React.FC<RightsTypeSelectorProps> = ({
  selectedType,
  onSelect,
}) => {
  const { t } = useTranslation('@pages/data-rights');

  return (
    <Stack gap="md">
      <Text size="lg" fw={600}>
        {t('dataRights.selectRight')}
      </Text>
      <Text size="sm" className="text-(--text-secondary)">
        {t('dataRights.selectRightDescription')}
      </Text>

      <Radio.Group value={selectedType || ''} onChange={(value) => onSelect(value as DataRightType)}>
        <Stack gap="sm">
          {DATA_RIGHTS_OPTIONS.map((option) => (
            <Card
              key={option.type}
              padding="md"
              radius="md"
              withBorder
              style={{
                cursor: 'pointer',
                border: selectedType === option.type ? '2px solid var(--mantine-primary-color)' : undefined,
              }}
              onClick={() => onSelect(option.type)}
            >
              <Group wrap="nowrap">
                <Radio value={option.type} />
                <div style={{ flex: 1 }}>
                  <Text fw={500}>{t(option.titleKey)}</Text>
                  <Text size="sm" className="text-(--text-secondary)">
                    {t(option.descriptionKey)}
                  </Text>
                </div>
              </Group>
            </Card>
          ))}
        </Stack>
      </Radio.Group>
    </Stack>
  );
};
